import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import Pagination from "../../../components/Pagination/Pagination";
import FilterBar from "../../../components/FilterBar/FilterBar";
import Loader from "../../../components/Loader/Loader";
import { Edit,PhoneCall,Trash2 } from "lucide-react";
import ApiService from "../../../services/Api.service";
import ConfirmDeleteModal from "../../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import AddUserModel from "./AddUserModel";
import AuthService from "../../../services/Auth.service";
import SkeletonLoader from "../../../components/SkeletonLoader/SkeletonLoader";

export default function Users() {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [postsPerPage, setPostsPerPage] = useState(20);
  const [headers, setHeaders] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    size: 20,
    total: 0,
    total_pages: 0,
  });

  const validationSchema = yup.object().shape({});
  const { handleSubmit, control } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const fetchDetail = async (page = 1, size = postsPerPage) => {
    try {
      setLoading(true);
      const res = await AuthService.getTrackerUsers({ page, size });

      if (res.status === 200 && res.data) {
        setTableData(res.data.data || []);
        setPagination(res.data.pagination || {});
        setHeaders(res.data.header || []);
      } else {
        toast.error("Failed to fetch Users");
      }
    } catch (error) {
      console.error("Error fetching Users:", error);
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, []);

  const handleSubmitFilter = async () => {
    await fetchDetail(1, postsPerPage, );
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSaveUsers = async (data) => {
    try {
      let res;
      if (isEditing && selectedItem) {
        res = await AuthService.updateTrackerUsers(selectedItem.id, data);
        if (res.status === 200) toast.success("User updated successfully");
        else toast.error("Failed to update User");
      } else {
        res = await AuthService.createTrackerUser(data);
        if (res.status === 200 || res.status === 201)
          toast.success("User added successfully");
        else toast.error("Failed to add User");
      }

      setShowModal(false);
      fetchDetail(pagination.page, postsPerPage);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save User");
    }
  };

  const handleDeleteTrackerUser = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const res = await AuthService.deleteTrackerUsers(deleteTarget.id);

      if (res.status === 200 || res.status === 204) {
        toast.success("User deleted successfully");
        fetchDetail(pagination.page, postsPerPage);
      } else {
        toast.error("Failed to delete User");
      }
    } catch (error) {
      console.error("Error deleting User:", error);
      toast.error("Error deleting User");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPostsPerPage(newSize);
    fetchDetail(pagination.page, newSize);
  };

  const mistableTh = {
    backgroundColor: "transparent",
    color: "rgba(43, 58, 103, 1)",
    fontFamily: "Nunito",
    fontSize: "13px",
    fontWeight: 700,
    textAlign: "center",
  };

  const tableTdStyles = {
    backgroundColor: "#ffffff",
    fontFamily: "Nunito",
    fontSize: "12px",
    fontWeight: "700",
    textAlign: "center",
    color: "#232122",
  };
  const toggleLock = async (item) => {
    try {
      const response = await AuthService.updateUnassignedNumber(item.id);
      if (response?.status === 200) {
        const updatedData = tableData.map((num) =>
          num.id === item.id ? { ...num, is_enabled: !num.is_enabled  ,assigned: "--", } : num
        );
        setTableData(updatedData);
      }
      toast.success(
        response?.data?.message || "User status updated successfully"
      );
    } catch (error) {
      console.error("Error updating User status:", error);
      toast.error("Failed to update User status");
    }
  };


  return (
    <>
      {/* Skeleton loader used inline inside the table container below instead of blocky full-screen spinner */}

      <div className="main-contanier">
        <FilterBar
          control={control}
          handleSubmit={handleSubmit}
          onSubmit={handleSubmitFilter}
          onClearFilters={() => fetchDetail(1, postsPerPage)}
          filters={[]}
          title="Users List"
          totalCount={pagination.total || 0}
          tableId="call-numbers-table"
          isDownload={false}
          isAdd={true}
          onAdd={handleAdd}
        />

        <div className="table-container">
          {loading ? (
            <SkeletonLoader type="table" rows={8} cols={5} />
          ) : (
            <table id="call-numbers-table" className="custom-table-container">
              <thead
                className="table-head"
                style={{ position: "sticky", top: "0", zIndex: "1" }}
              >
                <tr>
                  <th style={mistableTh}>S.No</th>
                  {headers.map((header, i) => (
                    <th key={i} style={mistableTh}>
                      {header.label}
                    </th>
                  ))}
                  <th style={mistableTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr key={index}>
                      <td style={tableTdStyles}>{index + 1}</td>
                      {headers.map((header, i) => (
                        <td key={i} style={tableTdStyles}>
                          {item[header.value] ?? "--"}
                        </td>
                      ))}

                      <td style={tableTdStyles}>
                        <button
                          className="icon-btn edit-btn"
                          onClick={() => handleEdit(item)}
                          title="Edit User"
                        >
                          <Edit size={18} color="#f4a261" />
                        </button>

                      <button
                        className={`toggle-icon-btn ${item.is_enabled ? "active" : "inactive"}`}
                        onClick={() => toggleLock(item)}
                        title={item.is_enabled ? "Disable User" : "Enable User"}
                      >
                        {item.is_enabled ? <PhoneCall title={item.is_enabled ? "Disable User" : "Enable User"}  size={18} /> : <></>}
                      </button>
                        <button
                          className="icon-btn delete-btn"
                          onClick={() => handleDeleteTrackerUser(item)}
                          title="Delete User"
                          style={{ marginLeft: "8px" }}
                        >
                          <Trash2 size={18} color="#e63946" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={headers.length + 2} style={tableTdStyles}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Add / Edit Modal */}
       {showModal && (
        <AddUserModel
          onClose={() => setShowModal(false)}
          onSave={handleSaveUsers}
          initialData={isEditing ? selectedItem : null}
          title={isEditing ? "Edit User" : "Add User"}
        />
      )}

        {showDeleteModal && (
          <ConfirmDeleteModal
            onClose={() => {
              setShowDeleteModal(false);
              setDeleteTarget(null);
            }}
            onConfirm={handleConfirmDelete}
            itemName={deleteTarget?.call_number || "this item"}
          />
        )}

        <Pagination
          pagination={pagination}
          postsPerPage={postsPerPage}
          handlePageSizeChange={handlePageSizeChange}
          fetchDetail={fetchDetail}
        />
      </div>
    </>
  );
}
